import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TeacherFilter from '../shared/teacher_filter';
import { Note, StatusCell, What, Who } from '../shared/cells';
import { fetch_email_outbox, fetch_email_switch } from '../api';
import { CheckChip, FilterBar, SelectFilter } from '../shared/controls';
import BulkSkip from '../shared/bulk_skip';
import { useStickyBarPadding } from '../shared/use_sticky_bar_padding';
import ModeBadge from '../email_send/mode_badge';
import SendDialog from '../email_send/send_dialog';
import {
  all_pick_state,
  mode_of_switch,
  outbox_resend_state,
  picked_ids,
  selection_error,
  toggle_all_ids,
  toggle_id
} from '../email_send/logic';
import { summary_line } from '../payouts/summary_line';
import {
  OUTBOX_KINDS,
  OUTBOX_STATUSES,
  outbox_filter_summary,
  outbox_filters_on,
  outbox_view,
  read_error
} from './adapters';
import SwitchCard from './switch_card';
import {
  PAYMENT_EMAILS_URL_KEYS,
  named_teacher,
  parse_payment_emails_filters,
  payment_emails_query,
  teacher_summary_name
} from './url_filters';
import { useInitialQuery, useQueryMirror } from '../shared/use_query_mirror';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { collect_all_pages } from '../../../util_components/csv_export/collect_pages';
import { OUTBOX_COLUMNS, payment_emails_csv_name } from './csv';
import '../payment_details/payment_details.css';
import './payment_emails.css';

/*
 * Never the email body or a payment detail. props: on_error(e, fallback) (the shell: a 401/403
 * means no module grant), on_notify(type, message).
 */
const OUTBOX_FAILED_TEXT = 'Could not load the payment email history.';
const SWITCH_FAILED_TEXT = 'Could not read the payment email switch.';
const NO_TEACHER = { teacher_uuid: '', display: '' };

// List-row pattern: teacher and when · which email · status; the recipients run under the row.
const COLUMNS = [
  {
    key: 'teacher',
    role: 'identifier',
    label: 'Teacher',
    track: { compact: '30%', full: '26%' },
    render: (r) => (
      <Who
        name={r.teacher_name}
        meta={[`Queued ${r.created_at}`, r.sent_at ? `Sent ${r.sent_at}` : null]}
      />
    )
  },
  {
    key: 'kind',
    label: 'Email',
    render: (r) => (
      <What
        title={r.kind_label}
        lines={[
          r.pay_month ? `Month ${r.pay_month}` : null,
          r.subject || null,
          `Email #${r.id}${r.origin ? ` · ${r.origin}` : ''}`
        ]}
      />
    )
  },
  {
    key: 'status',
    role: 'status',
    label: 'Status',
    track: { compact: '200px', full: '240px' },
    render: (r) => (
      <div className="tp-pe-status">
        <StatusCell
          chip={<StatusPill tone={r.status_tone}>{r.status_label}</StatusPill>}
          meta={[
            r.status_reason || null,
            r.next_attempt_at ? `Next try ${r.next_attempt_at}` : null,
            r.attempts > 1 ? `${r.attempts} attempts` : null
          ]}
        />
      </div>
    )
  },
  {
    // What the status means (or why it failed): a sentence, so a row line, not the status track.
    key: 'status_note',
    label: 'Status note',
    line: { from: 'kind', bare: true },
    render: (r) => (r.status_note ? <Note warn={r.status_note_warn} text={r.status_note} /> : null)
  },
  {
    key: 'to',
    label: 'Sent to',
    line: { from: 'kind', bare: true },
    render: (r) => (
      <Note
        label="To"
        text={r.to.length ? r.to.join(', ') : '—'}
        by={r.cc.length ? `Copy: ${r.cc.join(', ')}` : null}
      />
    )
  }
];

const BULK_BAR_ID = 'tp-pe-bulk';

// The outbox "Send again" bar: sticky while emails are picked (the Overview bulk-bar pattern).
const SendAgainBar = ({ ids, on_send, on_clear }) => {
  const bar_ref = useRef(null);
  useStickyBarPadding(bar_ref, ids.length > 0);
  if (!ids.length) return null;
  const err = selection_error(ids.length);
  return (
    <div
      ref={bar_ref}
      id={BULK_BAR_ID}
      tabIndex={-1}
      className="tp-bulkbar tp-bulkbar-sticky"
      role="region"
      aria-label="Payment email bulk actions"
    >
      <span className="tp-bulkbar-sum">
        <b>{ids.length} selected</b>
      </span>
      <button
        type="button"
        className="tp-btn"
        onClick={err ? undefined : on_send}
        aria-disabled={err ? 'true' : undefined}
        title={err || undefined}
      >
        Send again
      </button>
      <button type="button" className="tp-link" aria-label="Clear selection" onClick={on_clear}>
        Clear
      </button>
      {err ? <span className="tp-neg">{err}</span> : null}
    </div>
  );
};

const PaymentEmailsTab = ({ on_error, on_notify }) => {
  const [switch_data, setSwitchData] = useState(null);
  const [switch_error, setSwitchError] = useState('');
  const url = useInitialQuery(parse_payment_emails_filters);
  const [teacher, setTeacher] = useState(url.teacher);
  const [status, setStatus] = useState(url.status);
  const [kind, setKind] = useState(url.kind);
  const [list, setList] = useState({ state: 'loading', rows: [], next: null });
  const [more_busy, setMoreBusy] = useState(false);
  const [list_error, setListError] = useState('');
  const [picked, setPicked] = useState({});
  const [sending, setSending] = useState(null); // ids in the open confirm step
  const all_box = useRef(null);
  const switch_seq = useRef(0);
  const list_seq = useRef(0);

  const load_switch = useCallback(() => {
    switch_seq.current += 1;
    const mine = switch_seq.current;
    setSwitchError('');
    fetch_email_switch()
      .then((d) => {
        if (mine === switch_seq.current) setSwitchData(d);
      })
      .catch((e) => {
        if (mine !== switch_seq.current) return;
        const v = read_error(e, SWITCH_FAILED_TEXT);
        if (v.kind === 'permission') on_error(e, SWITCH_FAILED_TEXT);
        else setSwitchError(v.text);
      });
  }, [on_error]);

  const load_list = useCallback(
    (before_id) => {
      list_seq.current += 1;
      const mine = list_seq.current;
      if (before_id) setMoreBusy(true);
      else {
        setList((l) => ({ ...l, state: 'loading' }));
        setPicked({}); // a new filter never carries picks it no longer shows
      }
      setListError('');
      fetch_email_outbox({ teacher_uuid: teacher.teacher_uuid, status, kind }, before_id)
        .then((d) => {
          if (mine !== list_seq.current) return;
          const v = outbox_view(d);
          setMoreBusy(false);
          setList((l) => ({
            state: 'ready',
            rows: before_id ? l.rows.concat(v.rows) : v.rows,
            next: v.next_before_id
          }));
        })
        .catch((e) => {
          if (mine !== list_seq.current) return;
          setMoreBusy(false);
          const v = read_error(e, OUTBOX_FAILED_TEXT);
          if (v.kind === 'permission') {
            on_error(e, OUTBOX_FAILED_TEXT);
            return;
          }
          setListError(v.text);
          // A failed "Older emails" keeps the rows already shown.
          if (!before_id) setList({ state: 'error', rows: [], next: null });
        });
    },
    [teacher.teacher_uuid, status, kind, on_error]
  );

  useEffect(() => {
    load_switch();
  }, [load_switch]);

  useEffect(() => {
    load_list(null);
  }, [load_list]);

  const filters = { teacher_uuid: teacher.teacher_uuid, status, kind };
  useQueryMirror(PAYMENT_EMAILS_URL_KEYS, payment_emails_query(filters));
  // A teacher from the link shows by name once a loaded row names them.
  useEffect(() => {
    setTeacher((t) => named_teacher(t, list.rows));
  }, [list.rows]);
  const clear_filters = () => {
    setTeacher(NO_TEACHER);
    setStatus('');
    setKind('');
  };

  const on_switch_changed = (d) => {
    setSwitchData(d);
    if (d && typeof d.live_enabled === 'boolean') {
      on_notify('success', `Sending to teachers is now ${d.live_enabled ? 'ON' : 'OFF'}.`);
    }
  };

  const send_mode = mode_of_switch(switch_data);
  const ids = picked_ids(picked);
  const pick = all_pick_state(picked, list.rows);
  useLayoutEffect(() => {
    if (all_box.current) all_box.current.indeterminate = pick.some;
  }, [pick.some]);
  const columns = [
    {
      key: 'select',
      role: 'select',
      label: <span className="sr-only">Select</span>,
      track: '34px',
      render: (r) => {
        const st = outbox_resend_state(r);
        return (
          <input
            type="checkbox"
            aria-label={`Select email #${r.id} to ${r.teacher_name}`}
            checked={!!picked[r.id]}
            disabled={!st.selectable}
            title={st.reason || undefined}
            onChange={() => setPicked((p) => toggle_id(p, r.id))}
          />
        );
      }
    },
    ...COLUMNS
  ];

  return (
    <div className="tp-pe-tab">
      <SwitchCard
        data={switch_data}
        load_error={switch_error}
        on_retry={load_switch}
        on_changed={on_switch_changed}
        on_stale={load_switch}
      />

      <FilterBar
        label="Payment email filters"
        summary={summary_line(
          outbox_filter_summary({ list, filters, teacher_name: teacher_summary_name(teacher) })
        )}
      >
        <div className="tp-fsearch">
          <TeacherFilter
            id="tp-pe-filter-teacher"
            label="Teacher"
            value={teacher}
            onChange={setTeacher}
          />
        </div>
        <SelectFilter
          id="tp-pe-filter-status"
          label="Status"
          aria_label="Filter by status"
          value={status}
          all_label="All statuses"
          options={OUTBOX_STATUSES}
          onChange={setStatus}
        />
        <SelectFilter
          id="tp-pe-filter-kind"
          label="Email"
          aria_label="Filter by email type"
          value={kind}
          all_label="All emails"
          options={OUTBOX_KINDS}
          onChange={setKind}
        />
        {outbox_filters_on(filters) ? (
          <button type="button" className="tp-link tp-fbar-end" onClick={clear_filters}>
            Clear filters
          </button>
        ) : null}
      </FilterBar>

      <div className="tp-card tp-pe-list">
        <div className="tp-card-hd">
          <span className="tp-card-hd-main">
            <span className="tp-card-hd-title" role="heading" aria-level={2}>
              Payment emails
            </span>
            <span className="tp-card-hd-sub">
              Every payment email to a teacher, newest first, and where they are sent.
            </span>
          </span>
          <span className="tp-card-hd-acts">
            <ModeBadge mode={send_mode ? send_mode.mode : null} />
            {list.state === 'ready' && pick.any ? (
              <CheckChip
                inputRef={all_box}
                checked={pick.all}
                onChange={() => setPicked((p) => toggle_all_ids(p, list.rows))}
              >
                Select all shown
              </CheckChip>
            ) : null}
            <DownloadCsvButton
              columns={OUTBOX_COLUMNS}
              load_rows={() =>
                collect_all_pages((before_id) =>
                  fetch_email_outbox(filters, before_id).then((d) => {
                    const v = outbox_view(d);
                    return { rows: v.rows, next: v.next_before_id };
                  })
                )
              }
              file_name={payment_emails_csv_name}
              on_error={(e, fallback) => {
                if (read_error(e, fallback).kind === 'permission') on_error(e, fallback);
              }}
            />
          </span>
        </div>
        {list.state === 'loading' ? <CustomLoading /> : null}
        {list.state === 'error' ? (
          <div className="tp-pe-body">
            <LoadFailed text={list_error} on_retry={() => load_list(null)} />
          </div>
        ) : null}
        {list.state === 'ready' ? (
          <>
            <BulkSkip count={ids.length} target_id={BULK_BAR_ID} />
            <DataTable
              columns={columns}
              rows={list.rows}
              rowClassName={(r) => (picked[r.id] ? 'tp-row-sel' : '')}
              rowKey={(r) => r.uuid || String(r.id)}
              emptyText={
                outbox_filters_on(filters)
                  ? 'No payment emails match these filters.'
                  : 'No payment emails yet.'
              }
              card_below={660}
            />
            {list_error ? (
              <div className="tp-pe-body">
                <LoadFailed text={list_error} on_retry={() => load_list(list.next)} />
              </div>
            ) : null}
            {list.next ? (
              <div className="tp-pd-more tp-pe-more-row">
                <button
                  type="button"
                  className="tp-btn-out"
                  onClick={more_busy ? undefined : () => load_list(list.next)}
                  aria-disabled={more_busy ? 'true' : undefined}
                >
                  {more_busy ? 'Loading…' : 'Older emails'}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <SendAgainBar ids={ids} on_send={() => setSending(ids)} on_clear={() => setPicked({})} />

      {sending ? (
        <SendDialog
          title={`Send again: ${sending.length} ${sending.length === 1 ? 'email' : 'emails'}`}
          target={{ type: 'outbox', ids: sending }}
          on_close={() => setSending(null)}
          on_sent={() => {
            setPicked({});
            load_list(null);
          }}
          on_error={on_error}
          on_notify={on_notify}
        />
      ) : null}
    </div>
  );
};

export default PaymentEmailsTab;
